/**
 * לקוח — one customer, on the Control Plane. `/platform/tenants/{tenant_uid}`.
 *
 * 04 §13.2.1 bounds what may appear here in one sentence: *"View tenant metadata: name, status,
 * plan, counts, contacts. **Never business data**."* So this screen carries a status, two counts and
 * a subscription state, and there is no field on it that a company's numbers could reach.
 *
 * The COUNTS ARE NOT A LINK INTO THE OFFICE. A platform operator who wants to see inside a tenant
 * needs a support grant — an approved, time-bounded, always-audited thing (04 §13.1.5 / §6.4) — and
 * the absence of a "open this office" button here is that rule being structural rather than
 * remembered. OA-0001 §3.2 is the other half: GL-POINT must NOT be the one deciding which employee of
 * this customer may see Company X, so there is nothing on this screen that could.
 *
 * ── WHAT THIS SCREEN GAINED, AND WHY IT BELONGS HERE ────────────────────────────────────────────
 *
 * Suspension, reactivation and closure were server routes with no browser path, and this screen said
 * so in a written state — *"פעולות שטרם נפתחו בקונסולה"*. That state was honest and it was not a
 * product: an operator holding `platform.tenant.suspend` had no way to suspend anybody.
 *
 * They live HERE and not on the customer list, because each one is a decision about ONE named
 * customer and the operator has to be looking at that customer's card — its status, its counts, its
 * subscription — when they make it. A suspend button on a list row is a suspend button pressed on
 * the wrong row.
 *
 * ── EVERY ONE OF THEM IS A MODAL, AND THAT PASSES THE SIX TESTS ────────────────────────────────
 *
 * 09 §2.1: one decision (M3), one field — the reason (M2), no table (M4), no drill-down (M6),
 * nothing lost by pressing Escape (M5), and it changes state (M1). The blast radius is stated in
 * words before the click, and for suspension the sentence a platform operator most needs to be able
 * to give a customer is in it: **nothing is deleted**. OA-0001 §3.11.
 *
 * ── AND THE REFUSALS ARE NOT GENERIC ───────────────────────────────────────────────────────────
 *
 * The server answers a refused lifecycle move with the CURRENT status and the statuses the move is
 * permitted from. `lifecycleText.ts` turns those into "this customer is already suspended" and "the
 * action is possible from פעיל · תקופת ניסיון" — the difference between an operator who now knows
 * the job is done and an operator who opens a ticket about a working product.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * AND THEN THE OTHER HALF: WHAT HAPPENS WHEN THE CUSTOMER ACTUALLY LEAVES
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `platform.tenant.close` moves ONE COLUMN, and that is correct — *"a lifecycle change is a state
 * change and nothing else."* It is also only the commercial half. On the day a customer leaves, with
 * nothing but the three buttons above, their connector credential is still active on a machine
 * nobody watches and still reading their accounting database; their source connections still hold
 * live logins into it; every session is still live; and every unaccepted invitation still mints a
 * new authenticated principal. The offboarding surface is the half that closes those, and it belongs
 * on THIS card for the same reason the three buttons above do: it is a decision about one named
 * customer, and the operator has to be looking at that customer when they make it.
 *
 * ── THE THREE THINGS THIS SECTION EXISTS TO SAY ─────────────────────────────────────────────────
 *
 * 1. **Termination is not deletion.** Nothing is destroyed — the service's persistence port has no
 *    method that could. An operator who believes otherwise either avoids a correct action for months
 *    or takes a catastrophic one believing it can be undone, and both cost more than the sentence.
 *
 * 2. **What undoing costs is not a yes/no.** The server sends a UNION, and each member is a
 *    different conversation: free · until a stated instant · the window has passed and nothing is
 *    destroyed yet · NOT KNOWABLE because no policy can be read · gone. The fourth is the one a
 *    boolean would render as "cannot be undone", and it is not a refusal — it is an unanswered
 *    question with a named cause.
 *
 * 3. **There is no purge button, and the screen says why.** Deletion eligibility is a READ. There is
 *    no capability that authorises destroying a customer's data, therefore no route and no control.
 *    A missing button nobody explains reads as one somebody forgot.
 *
 * Nothing here decides permission, and nothing here computes a stage, a window or an eligibility. It
 * renders answers that arrived, through `offboardingText.ts`, and posts two writes.
 */

import { useCallback, useState } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { FactList } from '../components/FactList';
import { Modal } from '../components/Modal';
import { StateBlock, ErrorState } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import type {
  ExportDispositionKind,
  OffboardingStateView,
  RevocationTally,
  TenantLifecycleRefusal,
  TenantStatus,
} from '../api/contract';
import { EXPORT_DISPOSITION_KINDS, TENANT_STATUSES } from '../api/contract';
import { subscriptionStateHe, tenantStatusHe } from '../api/adminVocabulary';
import type { TenantLifecycleAction } from '../api/lifecycleText';
import { tenantLifecycleActionText, tenantLifecycleRefusalText } from '../api/lifecycleText';
import type { OffboardingAction } from '../api/offboardingText';
import {
  EXPORT_DISPOSITION_REQUIRED_HE,
  PURGE_HAS_NO_ACTION_HE,
  REINSTATEMENT_AUTHORITY_HE,
  RETENTION_ABSENT_HE,
  TERMINATION_DESTROYS_NOTHING_HE,
  TERMINATION_REVOKES,
  exportDispositionText,
  mustReestablishLines,
  offboardingActionText,
  offboardingRefusalOf,
  offboardingRefusalText,
  purgeText,
  retentionPolicyText,
  revocationLines,
  stageEnteredHe,
  stageHe,
  stageReasonHe,
  stageReversalText,
} from '../api/offboardingText';
import { ApiError, ConflictError, DeniedError, revocationTallyOf } from '../api/client';
import { callOperation } from '../api/operations';
import type { ExportDispositionBody } from '../api/operations';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { DenialNotice } from '../components/DenialNotice';
import type { DenyBody } from '../api/contract';
import { ServerRead } from '../components/ServerRead';
import { listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';

export interface PlatformTenantScreenProps {
  readonly tenantUid: string;
}

const TITLE_HE = 'כרטיס לקוח';

/**
 * NO PATH SEGMENTS. There used to be two tables here mapping an action to a URL fragment, and the
 * address was assembled from one of them at the call site. Both are gone: the address is now built
 * by `callOperation` from `registry/routes.baseline.json`, and each action names its OPERATION.
 *
 * The dispatch below is written as three (and two) separate `callOperation` calls rather than one
 * call taking a computed id, and that is deliberate. A computed id is invisible to the reachability
 * scan, so "the operator can suspend a customer" would rest on the same kind of inference the
 * screen-name map rested on. Spelled out, deleting the close control fails CI by itself.
 *
 * There is still no `purge` anywhere: `purge-eligibility` is a GET this screen reads, and nothing on
 * this surface can destroy anything.
 */

type Outcome =
  | { readonly kind: 'applied'; readonly action: TenantLifecycleAction }
  | { readonly kind: 'refused'; readonly refusal: TenantLifecycleRefusal }
  | { readonly kind: 'denied'; readonly body: DenyBody }
  | { readonly kind: 'error'; readonly requestUid: string }
  | { readonly kind: 'unreachable' };

/**
 * The offboarding outcomes, kept apart from the lifecycle ones above.
 *
 * `terminated` carries the tally because the counts ARE the answer to "what just happened", and
 * `reinstated` carries what must be re-established because *"reinstated: true" on its own would read
 * as a restoration, which is exactly what did not happen*.
 */
type OffboardingOutcome =
  | { readonly kind: 'terminated'; readonly revoked: RevocationTally | null }
  | { readonly kind: 'reinstated'; readonly mustReestablishHe: readonly string[] }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

/** Only the statuses the server actually sent, and only ones this build has Hebrew for. */
function knownStatuses(values: readonly string[] | undefined): readonly TenantStatus[] {
  const known: readonly string[] = TENANT_STATUSES;
  return (values ?? []).filter((v): v is TenantStatus => known.includes(v));
}

export function PlatformTenantScreen({ tenantUid }: PlatformTenantScreenProps): JSX.Element {
  const ctx = useWorkContext();
  const [pendingAction, setPendingAction] = useState<TenantLifecycleAction | null>(null);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [pendingOffboarding, setPendingOffboarding] = useState<OffboardingAction | null>(null);
  const [offboardingReason, setOffboardingReason] = useState('');
  // NO DEFAULT. An omitted export disposition would silently become a waiver on the wire, and a
  // right that disappears when a field is omitted is not a right.
  const [exportKind, setExportKind] = useState<ExportDispositionKind | null>(null);
  const [exportDetail, setExportDetail] = useState('');
  const [offboardingOutcome, setOffboardingOutcome] = useState<OffboardingOutcome | null>(null);

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  /*
   * READ FROM THE SERVER. **Phase 2 §6, vertical D.**
   *
   * Two reads, because they answer two questions:
   *
   *   · `platform.tenant.read` supplies the customer record. The card used to find it in
   *     `fixtureTenants`, which meant every wired lifecycle write on this screen — suspend, close,
   *     reactivate, terminate, reinstate — was performed against a uid from a local constant.
   *   · `platform.tenant.offboarding.read` supplies the STAGE, the reversal window and the
   *     retention policy. The screen said so in a comment — "with apps/api running this is
   *     platform.tenant.offboarding.read" — and now it is.
   *
   * The retention policy is the one that must never be approximated: 0011 gives it NO DEFAULT day
   * counts, and `retentionPolicyText` renders a missing policy as the absence it is. A fixture there
   * put invented retention periods in front of an operator about to terminate a customer.
   */
  const readTenants = useCallback(() => callOperation('platform.tenant.read', {}), []);
  const readOffboarding = useCallback(
    () =>
      callOperation('platform.tenant.offboarding.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  const tenantsRead = useServerData(readTenants);
  const offboardingRead = useServerData(readOffboarding);

  const tenant = listOf(tenantsRead.result, 'tenants').find((t) => t.tenantUid === tenantUid) ?? null;
  const back = { labelHe: 'הלקוחות', href: routes.platformTenants };

  if (tenantsRead.result.state !== 'loaded') {
    /*
     * NOT YET AN ANSWER, so not yet "unavailable".
     *
     * `tenant` is derived by searching the list, and an absent list yields `null` — which is the
     * same value a genuinely unknown uid yields. Falling into the block below while the read is
     * still outstanding would tell an operator the customer does not exist because the page had not
     * finished loading; falling into it after a refusal would say the same thing about a customer
     * this operator merely may not see.
     */
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <ServerRead result={tenantsRead.result} whatHe="את פרטי הלקוח" skeletonRows={6}>
          {() => null}
        </ServerRead>
      </AppShell>
    );
  }

  if (tenant === null) {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        {/*
          09 §5.8: not a raw 404 and not a blank screen. The wording is careful not to confirm or
          deny that a tenant with this uid exists anywhere — that is the gate's disclosure rule, and
          a screen that leaked it would undo it. It is reached ONLY after a successful read, so it
          is a statement about the uid and never about the request.
        */}
        <StateBlock
          titleHe="הלקוח הזה אינו זמין"
          bodyHe="לא הצלחנו להציג את הלקוח שאליו מפנה הקישור."
          tone="unavailable"
          nextStep={
            <a className="btn btn--primary" href={back.href}>
              חזרה לרשימת הלקוחות
            </a>
          }
        />
      </AppShell>
    );
  }

  const status = tenantStatusHe(tenant.status);
  const subscription = subscriptionStateHe(tenant.subscriptionState);

  function closeModal(): void {
    setPendingAction(null);
    setReason('');
  }

  async function apply(action: TenantLifecycleAction): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      // A write. Never retried — the client cannot know whether the server committed, and a repeated
      // suspension is a second audit row against a customer who was suspended once.
      const params = { tenant_uid: tenantUid };
      if (action === 'suspend') {
        await callOperation('platform.tenant.suspend', { params, body: { reason } });
      } else if (action === 'reactivate') {
        await callOperation('platform.tenant.reactivate', { params, body: { reason } });
      } else {
        await callOperation('platform.tenant.close', { params, body: { reason } });
      }
      setOutcome({ kind: 'applied', action });
    } catch (err: unknown) {
      if (err instanceof ConflictError) {
        // The actionable half, as the server sent it. Nothing here decides what it means.
        setOutcome({
          kind: 'refused',
          refusal: {
            reason: err.body.error,
            currentStatus: knownStatuses(
              err.body.currentStatus === undefined ? [] : [err.body.currentStatus],
            )[0] ?? null,
            permittedFrom: knownStatuses(err.body.permittedFrom),
          },
        });
      } else if (err instanceof DeniedError) {
        setOutcome({ kind: 'denied', body: err.body });
      } else if (err instanceof ApiError && err.status === 0) {
        setOutcome({ kind: 'unreachable' });
      } else if (err instanceof ApiError) {
        setOutcome({ kind: 'error', requestUid: err.requestUid });
      } else {
        setOutcome({ kind: 'unreachable' });
      }
    } finally {
      setSending(false);
      closeModal();
    }
  }

  function closeOffboardingModal(): void {
    setPendingOffboarding(null);
    setOffboardingReason('');
    setExportKind(null);
    setExportDetail('');
  }

  /**
   * The two offboarding writes.
   *
   * The export disposition is BUILT FROM THE CHOICE and never defaulted — there is no `?? WAIVED`
   * here and there is no arm for "not answered", which is why the confirm control stays disabled
   * until one of the three has been selected and its detail supplied.
   */
  async function sendOffboarding(action: OffboardingAction): Promise<void> {
    setSending(true);
    setOffboardingOutcome(null);
    try {
      const params = { tenant_uid: tenantUid };
      if (action === 'reinstate') {
        const body = await callOperation('platform.tenant.offboarding.reinstate', {
          params,
          body: { reason: offboardingReason },
        });
        setOffboardingOutcome({
          kind: 'reinstated',
          mustReestablishHe: mustReestablishLines(body.mustReestablish),
        });
      } else {
        // NOT A `??`. An unanswered disposition does not become a waiver here, exactly as it does
        // not become one on the route: there is no arm for "not answered", so the request is simply
        // not built. The confirm control is guarded by the same condition.
        if (exportKind === null) return;
        const exportDisposition: ExportDispositionBody =
          exportKind === 'REQUESTED'
            ? { kind: 'REQUESTED' }
            : exportKind === 'ALREADY_DELIVERED'
              ? { kind: 'ALREADY_DELIVERED', exportRequestUid: exportDetail }
              : { kind: 'WAIVED', detail: exportDetail };
        const body = await callOperation('platform.tenant.offboarding.terminate', {
          params,
          body: { reason: offboardingReason, exportDisposition },
        });
        setOffboardingOutcome({ kind: 'terminated', revoked: revocationTallyOf(body.revoked) ?? null });
      }
    } catch (err: unknown) {
      // One classification, the same one every screen uses. No private `catch` vocabulary here.
      setOffboardingOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
      closeOffboardingModal();
    }
  }

  const modalCopy = pendingAction === null ? null : tenantLifecycleActionText(pendingAction);
  const refusalText = outcome?.kind === 'refused' ? tenantLifecycleRefusalText(outcome.refusal) : null;

  // From the server. `null` while the read is outstanding or after it failed — and the card below
  // renders that through `<ServerRead>` rather than as "this customer has no offboarding state",
  // which would read as "nothing has been terminated" about a customer that may have been.
  const offboarding: OffboardingStateView | null =
    offboardingRead.result.state === 'loaded' ? offboardingRead.result.data : null;
  const reversal = offboarding === null ? null : stageReversalText(offboarding.stage);
  const purge = offboarding === null ? null : purgeText(offboarding.purge);
  const retention = offboarding?.retention ?? null;
  const retentionText = retention === null ? null : retentionPolicyText(retention);
  const offboardingCopy =
    pendingOffboarding === null ? null : offboardingActionText(pendingOffboarding);
  const exportCopy = exportKind === null ? null : exportDispositionText(exportKind);
  const offboardingReady =
    offboardingReason.trim() !== '' &&
    (pendingOffboarding === 'reinstate' ||
      (exportCopy !== null && (exportCopy.detailLabelHe === null || exportDetail.trim() !== '')));

  return (
    <AppShell
      titleHe={tenant.legalName}
      backToParent={back}
      provenanceHe="הקונסולה מציגה נתוני ניהול בלבד: שם, מצב, תכנית ומונים. אין בה נתונים עסקיים של הלקוח."
    >
      <FactList
        facts={[
          { labelHe: 'שם הלקוח', value: tenant.legalName },
          { labelHe: 'מזהה ההקמה', value: <span dir="ltr">{tenant.slug}</span> },
          { labelHe: 'מצב הלקוח', value: <StatusChip labelHe={status.labelHe} tone={status.tone} /> },
          {
            labelHe: 'מצב המנוי',
            value: <StatusChip labelHe={subscription.labelHe} tone={subscription.tone} />,
          },
          { labelHe: 'חברות במשרד', value: <span dir="ltr">{tenant.companyCount}</span> },
          { labelHe: 'משתמשים במשרד', value: <span dir="ltr">{tenant.seatCount}</span> },
        ]}
      />

      <div className="actions-row">
        <a className="btn btn--primary" href={routes.platformTenantSubscription(tenant.tenantUid)}>
          מנוי ומכסות
        </a>
        <a className="btn" href={routes.platformSupportGrants}>
          הרשאות תמיכה
        </a>
      </div>

      {/*
        THE THREE LIFECYCLE ACTIONS.

        None of them is hidden by state. A hidden button is presentation and never a control — the
        gate and the service both decide again — and hiding "ביטול השעיה" on an active customer would
        mean an operator who believed a customer was suspended could not find out that they are not.
        What each button does at the wrong moment is say so, by name.
      */}
      <section className="notice" data-tone="info">
        <h2 className="notice__title">מחזור החיים של הלקוח</h2>
        <p className="notice__body">
          השעיה חוסמת פעולות ואינה מוחקת דבר: כל החברות, המשתמשים וההרשאות נשארים כפי שהם, והמשרד
          ממשיך להופיע ברשימת הלקוחות כדי שאפשר יהיה להחזירו. סגירה מסיימת את ההתקשרות המסחרית, וגם
          היא אינה מוחקת נתונים.
        </p>
        <div className="actions-row">
          <button type="button" className="btn" onClick={() => setPendingAction('suspend')}>
            {tenantLifecycleActionText('suspend').intentHe}
          </button>
          <button type="button" className="btn" onClick={() => setPendingAction('reactivate')}>
            {tenantLifecycleActionText('reactivate').intentHe}
          </button>
          <button
            type="button"
            className="btn btn--irreversible"
            onClick={() => setPendingAction('close')}
          >
            {tenantLifecycleActionText('close').intentHe}
          </button>
        </div>
      </section>

      {outcome === null ? null : outcome.kind === 'applied' ? (
        <StateBlock
          titleHe="הפעולה בוצעה"
          bodyHe={`${tenantLifecycleActionText(outcome.action).intentHe} נרשמה ביומן יחד עם הסיבה ועם מי שביצע אותה.`}
          nextStep={<p>מצב הלקוח יתעדכן בכרטיס בטעינה הבאה.</p>}
        />
      ) : refusalText !== null ? (
        <section className="denial" data-tone="blocked" role="alert">
          <h2 className="denial__title">{refusalText.titleHe}</h2>
          <p className="denial__explanation">{refusalText.explanationHe}</p>
          <p className="denial__explanation">{refusalText.unchangedHe}</p>
          <p className="denial__next-step">{refusalText.nextStepHe}</p>
        </section>
      ) : outcome.kind === 'denied' ? (
        <DenialNotice deny={outcome.body} />
      ) : outcome.kind === 'error' ? (
        <ErrorState requestUid={outcome.requestUid} />
      ) : (
        <StateBlock
          titleHe="הפעולה לא בוצעה"
          bodyHe="לא הצלחנו ליצור קשר עם השרת, ולכן מצב הלקוח לא השתנה."
          tone="error"
          nextStep={<p>אפשר לנסות שוב מאוחר יותר.</p>}
        />
      )}

      {/*
        ── THE OFFBOARDING SURFACE ──────────────────────────────────────────────────────────────

        Read the first notice before the controls: it is the sentence a platform operator most needs
        and most often does not have. Neither button is hidden by state, for the reason the three
        above are not — a hidden control explains nothing, and an operator who believes a customer
        has already been terminated must be able to find out that they have not.
      */}
      <section className="notice" data-tone="warn">
        <h2 className="notice__title">סיום התקשרות עם הלקוח</h2>
        <p className="notice__body">{TERMINATION_DESTROYS_NOTHING_HE}</p>
        <p className="notice__body">מה שנשלל בסיום התקשרות, לפי הסדר שבו הוא נשלל:</p>
        <ul className="notice__list">
          {TERMINATION_REVOKES.map((entry) => (
            <li key={entry.classKey}>
              <strong>{entry.labelHe}</strong> — {entry.detailHe}
            </li>
          ))}
        </ul>
        <p className="notice__body">{REINSTATEMENT_AUTHORITY_HE}</p>
        <div className="actions-row">
          <button
            type="button"
            className="btn btn--irreversible"
            onClick={() => setPendingOffboarding('terminate')}
          >
            {offboardingActionText('terminate').intentHe}
          </button>
          <button type="button" className="btn" onClick={() => setPendingOffboarding('reinstate')}>
            {offboardingActionText('reinstate').intentHe}
          </button>
        </div>
      </section>

      {/*
        THE OFFBOARDING CARD, AND WHY ITS FAILURE STATE IS NOT A SENTENCE ABOUT THE CUSTOMER.

        This block used to render one "unavailable" message for every reason the state was absent.
        That was honest as far as it went, but it is the wrong grain HERE: an operator standing in
        front of this card is deciding whether to terminate a customer, and "we could not read the
        stage" and "you may not read the stage" send them to two different people. `<ServerRead>`
        keeps them apart, and neither of them is ever rendered as a stage.
      */}
      <ServerRead
        result={offboardingRead.result}
        whatHe="את מצב תהליך הסיום"
        skeletonRows={4}
        renderNotFound={() => (
          <StateBlock
            titleHe="מצב תהליך הסיום אינו זמין"
            bodyHe="לא הצלחנו להציג באיזה שלב נמצא הלקוח הזה בתהליך הסיום, ולכן אין כאן קביעה לגבי החזרה."
            tone="unavailable"
            nextStep={<p>אפשר לרענן את המסך ולנסות שוב.</p>}
          />
        )}
      >
        {(state) => (
        <section className="notice" data-tone="info">
          <h2 className="notice__title">היכן הלקוח הזה עומד</h2>
          <FactList
            facts={[
              {
                labelHe: 'שלב בתהליך הסיום',
                value: (
                  <StatusChip
                    labelHe={stageHe(state.stage.stage).labelHe}
                    tone={stageHe(state.stage.stage).tone}
                  />
                ),
              },
              {
                labelHe: 'מה עולה החזרה מכאן',
                value:
                  reversal === null ? null : (
                    <StatusChip labelHe={reversal.labelHe} tone={reversal.tone} />
                  ),
                absentHe: 'אין מה להחזיר — ההתקשרות פעילה',
              },
              {
                // Shown ONLY where the server stated one. A window with no end is not a window, and
                // an invented "בקרוב" here would be the product guessing at a governed instant.
                labelHe: 'מועד אחרון להחזרה',
                value: reversal?.deadlineHe ?? null,
                absentHe: 'אין מועד — ראו ההסבר שלהלן',
              },
              {
                labelHe: 'הכניסה לשלב',
                value: state.stage.stage === 'ACTIVE' ? null : stageEnteredHe(state.stage),
                absentHe: 'לא נרשם מעבר שלב',
              },
              {
                labelHe: 'הסיבה שנרשמה',
                value: stageReasonHe(state.stage),
                absentHe: 'לא נרשמה סיבה',
              },
            ]}
          />
          <p className="notice__body">{stageHe(state.stage.stage).explanationHe}</p>
          {reversal === null ? null : (
            <>
              <p className="notice__body">{reversal.explanationHe}</p>
              {reversal.unknownHe === null ? null : (
                <p className="notice__body">{reversal.unknownHe}</p>
              )}
              {reversal.restoresHe === null ? null : (
                <p className="notice__body">{reversal.restoresHe}</p>
              )}
            </>
          )}

          {state.stage.stage !== 'TERMINATED' ? null : (
            <>
              <h3 className="notice__subtitle">מה נשלל בפועל</h3>
              <ul className="notice__list">
                {revocationLines(state.stage.revoked).map((line) => (
                  <li key={line.classKey}>
                    {line.labelHe}: <span dir="ltr">{line.count}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
        )}
      </ServerRead>

      {offboarding === null ? null : retention === null || retentionText === null ? (
        <StateBlock
          titleHe={RETENTION_ABSENT_HE.titleHe}
          bodyHe={RETENTION_ABSENT_HE.bodyHe}
          tone="unavailable"
          nextStep={<p>עד שתוגדר מדיניות שמירה, בקשת סיום התקשרות למשרד הזה תידחה.</p>}
        />
      ) : (
        <section className="notice" data-tone="info">
          <h2 className="notice__title">מדיניות השמירה שחלה על הלקוח</h2>
          <FactList
            facts={[
              { labelHe: 'על סמך מה נקבעה', value: retention.basis },
              { labelHe: 'חלון ההחזרה', value: retentionText.reinstatementWindowHe },
              { labelHe: 'מתי מותר להתחיל במחיקה', value: retentionText.deletionEligibilityHe },
              {
                labelHe: 'מיקום הנתונים',
                value: retentionText.residencyHe,
                absentHe: 'המדיניות אינה מציינת מיקום נתונים שאפשר לקרוא',
              },
              {
                labelHe: 'נקבעה בתאריך',
                value: retentionText.setAtHe,
                absentHe: 'לא נרשם מועד',
              },
            ]}
          />
          {retentionText.violationsHe.length === 0 ? null : (
            <>
              <h3 className="notice__subtitle">מה אינו תקין במדיניות</h3>
              {/* EVERY reason, never the first: an operator fixing one at a time asks twice. */}
              <ul className="notice__list">
                {retentionText.violationsHe.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {purge === null ? null : (
        <section className="notice" data-tone="info">
          <h2 className="notice__title">{purge.titleHe}</h2>
          <p className="notice__body">{purge.explanationHe}</p>
          {purge.blockersHe.length === 0 ? null : (
            <ul className="notice__list">
              {purge.blockersHe.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          <FactList
            facts={[
              {
                labelHe: 'מוקדם מכך אין מתחילים',
                value: purge.notBeforeHe,
                absentHe: 'לא נמסר מועד',
              },
              {
                labelHe: 'מה מחיקה הייתה משמידה',
                value: purge.deletionImplicationHe,
                absentHe: 'לא נמסר תיאור — התשובה נמסרת רק כשאין עוד התנגדות',
              },
            ]}
          />
          {/* No button, and the reason it is absent is on the screen rather than in a document. */}
          <p className="notice__body">{PURGE_HAS_NO_ACTION_HE}</p>
        </section>
      )}

      {offboardingOutcome === null ? null : offboardingOutcome.kind === 'terminated' ? (
        <StateBlock
          titleHe="ההתקשרות הסתיימה"
          bodyHe={TERMINATION_DESTROYS_NOTHING_HE}
          tone="stale"
          nextStep={
            offboardingOutcome.revoked === null ? (
              <p>השרת לא מסר את מניין מה שנשלל, ולכן אינו מוצג כאן.</p>
            ) : (
              <ul className="notice__list">
                {revocationLines(offboardingOutcome.revoked).map((line) => (
                  <li key={line.classKey}>
                    {line.labelHe}: <span dir="ltr">{line.count}</span>
                  </li>
                ))}
              </ul>
            )
          }
        />
      ) : offboardingOutcome.kind === 'reinstated' ? (
        <StateBlock
          titleHe="ההתקשרות הוחזרה — והגישה לא הוחזרה"
          bodyHe="ההחזרה מחזירה את ההתקשרות בלבד. להלן מה שצריך להיעשות מחדש, ובלעדיו איש אינו נכנס."
          nextStep={
            offboardingOutcome.mustReestablishHe.length === 0 ? (
              <p>השרת לא מסר מה צריך להיעשות מחדש, ולכן אין כאן רשימה — אין להסיק מכך שדבר לא נדרש.</p>
            ) : (
              <ul className="notice__list">
                {offboardingOutcome.mustReestablishHe.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )
          }
        />
      ) : (
        <ActionFailureNotice
          failure={offboardingOutcome.failure}
          // The conflict branch is the screen's own, because only this screen knows which route it
          // called. `offboardingRefusalText` is where the body becomes Hebrew; nothing is rendered raw.
          renderConflict={(failure) => {
            const text = offboardingRefusalText(offboardingRefusalOf(failure.body));
            return (
              <section className="denial" data-tone="blocked" role="alert">
                <h2 className="denial__title">{text.titleHe}</h2>
                <p className="denial__explanation">{text.explanationHe}</p>
                {/* Read this one first where it exists: the cascade already ran. */}
                {text.partialHe === null ? null : (
                  <p className="denial__explanation">{text.partialHe}</p>
                )}
                {text.revokedLines.length === 0 ? null : (
                  <ul className="notice__list">
                    {text.revokedLines.map((line) => (
                      <li key={line.classKey}>
                        {line.labelHe}: <span dir="ltr">{line.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {text.violationsHe.map((line) => (
                  <p key={line} className="denial__explanation">
                    {line}
                  </p>
                ))}
                {text.currentStageHe === null ? null : (
                  <p className="denial__explanation">{text.currentStageHe}</p>
                )}
                {text.commercialHe === null ? null : (
                  <p className="denial__explanation">{text.commercialHe}</p>
                )}
                {text.arcOwnerHe === null ? null : (
                  <p className="denial__explanation">{text.arcOwnerHe}</p>
                )}
                {text.reinstatableUntilHe === null ? null : (
                  <p className="denial__explanation">{`חלון ההחזרה הסתיים ב־${text.reinstatableUntilHe}.`}</p>
                )}
                {text.notBeforeHe === null ? null : (
                  <p className="denial__explanation">{`מחיקה מבוקרת אינה מתחילה לפני ${text.notBeforeHe}.`}</p>
                )}
                {text.unchangedHe === null ? null : (
                  <p className="denial__explanation">{text.unchangedHe}</p>
                )}
                <p className="denial__next-step">{text.nextStepHe}</p>
              </section>
            );
          }}
        />
      )}

      {offboardingCopy === null || pendingOffboarding === null ? null : (
        <Modal
          intentHe={offboardingCopy.intentHe}
          blastRadiusHe={`${offboardingCopy.intentHe} תחול על ${tenant.legalName}. ${offboardingCopy.blastRadiusHe}`}
          // Reason · disposition · the one detail a disposition may require. Never more.
          fields={pendingOffboarding === 'terminate' ? 3 : 1}
          irreversible={offboardingCopy.irreversible}
          confirmLabelHe={sending ? 'שולח…' : offboardingCopy.confirmLabelHe}
          onConfirm={() => {
            if (offboardingReady) void sendOffboarding(pendingOffboarding);
          }}
          onClose={closeOffboardingModal}
        >
          <label className="field">
            <span className="field__label">סיבת הפעולה</span>
            <input
              className="field__input"
              name="offboarding-reason"
              value={offboardingReason}
              onChange={(e) => setOffboardingReason(e.currentTarget.value)}
            />
            <span className="field__hint">{offboardingCopy.reasonHintHe}</span>
          </label>

          {pendingOffboarding !== 'terminate' ? null : (
            <>
              <fieldset className="field field--choice">
                <legend className="field__label">מה קורה עם הנתונים של הלקוח</legend>
                {EXPORT_DISPOSITION_KINDS.map((candidate) => (
                  <label key={candidate} className="choice">
                    <input
                      type="radio"
                      name="exportDisposition"
                      value={candidate}
                      checked={exportKind === candidate}
                      onChange={() => {
                        setExportKind(candidate);
                        setExportDetail('');
                      }}
                    />
                    <span>{exportDispositionText(candidate).labelHe}</span>
                  </label>
                ))}
                <span className="field__hint">{EXPORT_DISPOSITION_REQUIRED_HE}</span>
              </fieldset>

              {exportCopy === null ? null : (
                <p className="form__note">{exportCopy.hintHe}</p>
              )}

              {exportCopy === null || exportCopy.detailLabelHe === null ? null : (
                <label className="field">
                  <span className="field__label">{exportCopy.detailLabelHe}</span>
                  <input
                    className="field__input"
                    name="exportDispositionDetail"
                    value={exportDetail}
                    onChange={(e) => setExportDetail(e.currentTarget.value)}
                  />
                </label>
              )}
            </>
          )}

          {offboardingReady ? null : (
            <p className="form__note">
              הפעולה אינה נשלחת עד שהוזנה סיבה, ובסיום התקשרות — גם עד שנבחרה תשובה לשאלת הנתונים
              והושלם מה שהיא דורשת.
            </p>
          )}
        </Modal>
      )}

      {modalCopy === null || pendingAction === null ? null : (
        <Modal
          intentHe={modalCopy.intentHe}
          // 09 §2.2: the blast radius names the customer and says what will NOT happen.
          blastRadiusHe={`${modalCopy.intentHe} תחול על ${tenant.legalName}. ${modalCopy.blastRadiusHe}`}
          fields={1}
          irreversible={modalCopy.irreversible}
          confirmLabelHe={sending ? 'שולח…' : modalCopy.confirmLabelHe}
          onConfirm={() => void apply(pendingAction)}
          onClose={closeModal}
        >
          <label className="field">
            <span className="field__label">סיבת הפעולה</span>
            <input
              className="field__input"
              name="lifecycle-reason"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
            <span className="field__hint">
              הסיבה נרשמת ביומן יחד עם מי שביצע את הפעולה, והיא חובה — פעולה מסחרית ללא סיבה כתובה
              היא פעולה שאיש לא יוכל להסביר בדיעבד.
            </span>
          </label>
        </Modal>
      )}
    </AppShell>
  );
}
